import type React from "react";
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { CalendarToday, LocationOn } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { IStreamDetails } from "../../../types/streams";
import { formatDateToLongString } from "../../../utils/FormatDate";
import { formatTime } from "../../../utils/FormatTime";
import ButtonIcon from "../../../components/icons/PenPaperIcon";

interface StreamHeroV2Props {
  stream: IStreamDetails;
  completionScore: number;
  deceasedName: string;
}

const StreamHeroV2: React.FC<StreamHeroV2Props> = ({
  stream,
  completionScore,
  deceasedName,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const navigate = useNavigate();

  const serviceType = stream.booking?.service ?? "";
  const isFuneral =
    serviceType.toLowerCase() === "funeral" ||
    serviceType.toLowerCase() === "funeralhome";

  const displayTitle = isFuneral
    ? deceasedName || stream.title
    : stream.title || "Untitled Stream";

  const initials = displayTitle
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const address = [stream.address_one, stream.city].filter(Boolean).join(", ");

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      {/* Avatar */}
      <Avatar
        src={
          isFuneral
            ? (stream.booking?.deceased?.[0]?.person_image ?? undefined)
            : undefined
        }
        sx={{
          width: 80,
          height: 80,
          bgcolor: reduxTheme.primary_colors[500],
          fontSize: "1.3rem",
          fontWeight: "bold",
          flexShrink: 0,
        }}
      >
        {initials}
      </Avatar>

      {/* Title + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            noWrap
            sx={{ lineHeight: 1.2 }}
          >
            {displayTitle}
          </Typography>
          {serviceType && (
            <Chip
              label={serviceType}
              size="small"
              sx={{ fontSize: "0.8rem", height: 22, flexShrink: 0 }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mt: 0.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CalendarToday
              sx={{ fontSize: 15, color: "text.secondary", flexShrink: 0 }}
            />
            <Typography variant="body1" color="text.secondary">
              {formatDateToLongString(stream.date)}
              {stream.start ? ` · ${formatTime(stream.start)}` : ""}
            </Typography>
          </Box>

          {address && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <LocationOn
                sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
              />
              <Typography variant="body1" color="text.secondary" noWrap>
                {address}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Edit Stream">
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/stream-management/view/${stream.id}/edit`)
            }
            sx={{
              p: 0.75,
            }}
          >
            <ButtonIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={52}
            sx={{ color: "divider", position: "absolute" }}
          />
          <CircularProgress
            variant="determinate"
            value={completionScore}
            size={52}
            sx={{ color: reduxTheme.primary_colors[500] }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {completionScore}%
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Complete
        </Typography>
      </Box>
    </Box>
  );
};

export default StreamHeroV2;
